import { useState, useSyncExternalStore } from 'react';
import { getAnonKey } from '../lib/handle';
import type { Take } from '../lib/types';

type Draft = { text: string; pending: boolean; failed?: boolean; capacityBlocked?: boolean; delivery?: DraftDelivery };
export type DraftDelivery = { key: string; identity: string | null; text: string; settled?: boolean };
const EMPTY: Draft = { text: '', pending: false };
const drafts = new Map<string, Draft>();
// Active requests are separately bounded and never evicted by unsent drafts.
const pendingDrafts = new Map<string, Draft>();
const listeners = new Set<() => void>();
const recoveredTakeListeners = new Set<(take: Take) => void>();
let activeIdentity: string | null | undefined;
let sessionFallbackIdentity: string | null | undefined;

function existingIdentity(fallback: string | null): string | null {
  try { return localStorage.getItem('coyg_anon'); } catch {
    sessionFallbackIdentity ??= fallback;
    return sessionFallbackIdentity ?? null;
  }
}

function identityIsCurrent(identity: string | null): boolean {
  const current = existingIdentity(identity);
  if (current !== activeIdentity) {
    // Device erasure or replacement must never reveal the previous fan's text.
    drafts.clear();
    pendingDrafts.clear();
    activeIdentity = current;
  }
  return current === identity;
}

function publish(key: string, draft: Draft): void {
  drafts.delete(key);
  if (draft.text || draft.pending) drafts.set(key, draft);
  while (drafts.size > 8) {
    const oldest = drafts.keys().next().value;
    if (oldest === undefined) break;
    drafts.delete(oldest);
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Eight unsent drafts and at most eight active sends, exclusively in memory. */
export function useNorthBankDraft(room: string, matchId: string | null) {
  const [fallbackIdentity] = useState(getAnonKey);
  const identity = existingIdentity(fallbackIdentity);
  identityIsCurrent(identity);
  const key = JSON.stringify([identity, room, room === 'match' ? matchId : null]);
  const draft = useSyncExternalStore(subscribe, () => pendingDrafts.get(key) ?? drafts.get(key) ?? EMPTY, () => EMPTY);
  return {
    ...draft,
    setText(text: string) {
      if (identityIsCurrent(identity) && !pendingDrafts.has(key)) publish(key, { text: text.slice(0, 280), pending: false });
    },
    begin(text: string): DraftDelivery | null {
      if (!identityIsCurrent(identity) || pendingDrafts.has(key)) return null;
      if (pendingDrafts.size >= 8) {
        publish(key, { text, pending: false, capacityBlocked: true });
        return null;
      }
      const delivery = { key, identity, text };
      drafts.delete(key);
      pendingDrafts.set(key, { text, pending: true, delivery });
      listeners.forEach((listener) => listener());
      return delivery;
    },
  };
}

/** A late response may settle a draft after its screen has been unmounted. */
export function settleNorthBankDraft(delivery: DraftDelivery, saved: boolean): boolean {
  if (delivery.settled || !identityIsCurrent(delivery.identity)) return false;
  delivery.settled = true;
  if (pendingDrafts.get(delivery.key)?.delivery !== delivery) return false;
  pendingDrafts.delete(delivery.key);
  publish(delivery.key, { text: saved ? '' : delivery.text, pending: false, failed: !saved });
  return true;
}

export function subscribeToRecoveredNorthBankTakes(listener: (take: Take) => void): () => void {
  recoveredTakeListeners.add(listener);
  return () => recoveredTakeListeners.delete(listener);
}

export function announceRecoveredNorthBankTake(take: Take): void {
  recoveredTakeListeners.forEach((listener) => listener(take));
}
